import {
  ACTION_ARGS,
  ALL_LAYOUT_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_MENU_ARGS,
  INPUT_TRIGGER_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { action } from '@ember/object';
import { ensureSafeComponent } from '@embroider/util';
import { fn, hash } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { or } from 'ember-truth-helpers';
import { reads } from 'macro-decorators';
import { testAwareWaitForLater } from '../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../layout/flex/column.gjs';
import HelperText from '../core/helper-text.gjs';
import Multiselect from '../menu/multiselect.gjs';
import SearchLabel from './search/label.gjs';
import SearchTrigger from './search/trigger.gjs';
import Select from '../menu/select.gjs';
import Super from './menu-parent.gjs';
import closeOnClickOutside from '../../../modifiers/close-on-click-outside';

const LABEL_SELECTOR = 'spds-input-search-label';
const MENU_CLOSE_TIMEOUT = 300;

const METADATA = Metadata.define({
  description: `Allows user to search via typing in text.

### Contextual components
The label, trigger, menu, and helper text are passed into the search input as yielded components using the \`label\`, \`trigger\`, \`menu\` and \`helperText\` keys.
Both the multiselect and single select menu variants yield their own \`item\` and \`noResults\` components.

### Filtering
Both variants of the search input provide simple text matching filtering, where user input into the trigger text field will filter out any non-matching items by applying the \`hidden\` CSS class.

This simple filtering can be overridden by providing a custom \`@filter\` action to the input. When provided, the \`@filter\` action will be the only filtering applied to the menu items, meaning the custom filtering action must handle updating the inclusion or visibility of the items.

Any custom filtering action will receive the current value of the input as an argument, and should return an array representing the list of options shown in the menu. This is used by the input to automatically display the \`noResults\` component when no items are present in the list.

Alternatively, by passing the \`@disableAutofilter\` argument, the input will apply no filtering, allowing for customized filtering behavior to be added through the \`@onInput\` action, or for menu items to be controlled by an API query. In these cases, it is advised to manually implement the yielded \`noResults\` component from the menu.

### Basic menu
The basic menu creates a list of buttons that trigger the passed in \`@onChange\` action when clicked. Only one option can be selected at a time, and it is possible to deselect an option by clicking it when it is already selected. The \`@onChange\` action receives the click action from the button that was clicked.

Instances of the \`item\` component can be provided a \`@value\` argument that is assigned to the \`data-value\` attribute of the button. This can be accessed in the event object returned to \`@onChange\` through \`event.target.dataset.value\`. Alternatively, the menu can also use the text of the option to properly select the correct item. A search input can be instantiated with a preselected value using either of these values when the \`@value\` argument is passed to the \`Spds:Input::Search\` component.

### Multiselect menu
The multiselect variant of the search input uses the \`Spds::Input:Group::Checkbox\` to drive its list of options. Each \`item\` is a \`Spds::Input::Checkbox\` component that is automatically managed by the containing group component. Their state can be managed by passing \`@checked\`, \`@label\`, and \`@value\` attributes directly to each item.

A specialized checkbox for a \`Select all\` option will appear automatically if there are six or more options in the list. If customized label text is desired for this checkbox, there is a \`@selectAllLabel\` attribute that can be passed to the menu component.

Checkboxes trigger the passed in \`@onChange\` action when clicked. The action will receive the same data as the \`Spds::Input::Group::Checkbox\`, which is an object containing three values:

* \`event\` - the click event object
* \`groupName\` - the group name of the checkbox group
* \`values\` - an array of the values for the checked checkboxes

If a multiselect menu is initialized with values selected, it will emit a modified \`onChange\` action on insert that does not include the \`event\` object.

#### Select all behavior
The select all function for the multiselect menu works slightly differently if the list of menu items is filtered. It will only select items that are showing at the time the "Select all" checkbox is checked. If the filter is then changed, it is possible items may be added back to the list that have not been selected.

If the list of items displayed drops below the minimum number of items required to display the "Select all" checkbox, it will be hidden.`,
  extends: [
    pick(ACTION_ARGS, [
      { name: 'onChange', description: 'Called when selection changes' },
      'onClose',
      { name: 'onInput', description: 'Called on input change' },
      'onOpen',
      'onSelect',
    ]),
    pick(COMPONENT_STATE_ARGS, ['disabled', 'error', 'open']),
    pick(INPUT_FORM_ARGS, [
      'helperText',
      { name: 'label', initialValue: 'Label' },
      { name: 'placeholder', initialValue: 'Placeholder' },
      'required',
      'value',
    ]),
    pick(INPUT_MENU_ARGS, ['multiselect', 'noResultsText', 'retainSelection']),
    pick(INPUT_TRIGGER_ARGS, [{ name: 'menuOpenThreshold', defaultValue: 2 }]),
    pick(ALL_LAYOUT_ARGS, ['flex', { name: 'gap', defaultValue: '4px' }]),
  ],
  meta: {
    disableAutofilter: Arg.boolean({ description: 'Disable built-in filtering' }),
    filter: Arg.action({ description: 'Custom filter for menu items' }),
    onClearInput: Arg.action({ description: 'Called when input is cleared' }),
    selectedValues: Arg.array({
      description: 'Array of values for externally controlling multiselect checkbox state',
    }),
  },
  name: 'Search',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=23618-561671&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/71f93b-search-input',
  },
});

export default class SpdsInputSearch extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-search';
  multiselectMenuComponent = Multiselect;
  search = true;
  singleSelectMenuComponent = Select;

  @tracked filteredMenuItems = [];

  @reads('selected.length', 0) selectedCount;

  get autoFilter() {
    return !(this.args.disableAutofilter || this.args.filter);
  }

  get containerId() {
    return this.composeSubcomponentId('container');
  }

  get displayedMenuItemCount() {
    if (this.args.disableAutofilter) return -1;
    if (this.args.filter) return this.filteredMenuItems?.length ?? 0;

    // Basic automatic filtering is owned by the filterable menu, which computes
    // its own no-results state from the visible item count.
    return undefined;
  }

  get flex() {
    return this.argOrDefault('flex');
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get menuOpenThreshold() {
    return this.argOrDefault('menuOpenThreshold');
  }

  // Only custom filtering lives here. Basic automatic text filtering is owned by
  // the filterable menu (driven by the forwarded `@searchValue`).
  filterMenuItems = modifier(async () => {
    if (this.args.disableAutofilter || !this.args.filter) return;

    this.filteredMenuItems = await Promise.resolve(this.args.filter(this.value));
  });

  clearInputValue() {
    this._value = '';
    this.args.onClearInput?.();
  }

  clearInput = async ({ noFocus = false } = {}) => {
    if (this.open) {
      await this.handleClose();
      // wait for menu to hide before resetting filter
      await testAwareWaitForLater(MENU_CLOSE_TIMEOUT);
    }

    this.clearInputValue();

    if (this.isMultiselect) {
      this.clearSelection();
    } else {
      this.manageSingleSelectItem();
    }

    if (!noFocus) this.trigger.focus();
  };

  selectAll = ({ checked, groupName }) => {
    this.filteredMenuItems.forEach(option => (option.checked = checked));

    this.onChange({
      event,
      groupName,
      values: checked ? this.filteredMenuItems.map(option => option.value) : [],
    });
  };

  @action
  onInput({ target: { value } }) {
    this._value = value;

    if (value === '' && !this.isMultiselect) this.clearInput();

    this.args.onInput?.(...arguments);
  }

  @action
  toggleMenu() {
    if (this.value.length >= this.menuOpenThreshold) {
      this.toggle();
    }
  }

  <template>
    <FlexColumn
      @flex={{@flex}}
      @gap={{this.gap}}
      class={{this.classes}}
      data-test-spds-input-search-container
      id={{this.containerId}}
      {{closeOnClickOutside this.handleClose LABEL_SELECTOR}}
      {{this.filterMenuItems}}
      {{this.onInsert}}
      {{this.setupComponent}}
      {{this.setMenuPosition}}
      {{this.syncExternalSelection}}
      {{this.updateValueFromExternalSource}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          helperText=(component
            HelperText
            error=@error
            helperText=@helperText
            id=this.helperTextId
            registerComponent=(fn this.registerComponent "helperText")
            unregisterComponent=(fn this.unregisterComponent "helperText")
          )
          label=(component
            SearchLabel
            for=this.id
            id=this.labelId
            label=@label
            onClick=this.toggleMenu
            required=@required
            registerComponent=(fn this.registerComponent "label")
            unregisterComponent=(fn this.unregisterComponent "label")
          )
          menu=(component
            (ensureSafeComponent this.menuType)
            anchorElement=this.triggerContainer
            autoFilter=this.autoFilter
            displayedItemCount=this.displayedMenuItemCount
            id=this.menuId
            noResultsText=@noResultsText
            onClick=this.onChange
            open=this.open
            onSelect=(or @onSelect this.onSelect)
            registerComponent=(fn this.registerComponent "menuComponent")
            retainSelection=this.retainSelection
            searchValue=this.value
            selectAll=(if this.autoFilter undefined this.selectAll)
            unregisterComponent=(fn this.unregisterComponent "menuComponent")
          )
          trigger=(component
            SearchTrigger
            clearInput=this.clearInput
            clearSelection=this.clearSelection
            closeMenu=this.handleClose
            disabled=@disabled
            error=@error
            id=this.id
            open=this.open
            menuOpenThreshold=this.menuOpenThreshold
            multiselect=@multiselect
            onClose=this.handleClose
            onInput=this.onInput
            openMenu=this.handleOpen
            placeholder=@placeholder
            registerClearFilterComponent=(fn this.registerComponent "clearFilterComponent")
            registerComponent=(fn this.registerComponent "triggerComponent")
            required=@required
            selected=this.selected
            unregisterClearFilterComponent=(fn this.unregisterComponent "clearFilterComponent")
            unregisterComponent=(fn this.unregisterComponent "triggerComponent")
            value=this.value
          )
        )
      }}
    </FlexColumn>
  </template>
}

export const INPUT_SEARCH_METADATA = SpdsInputSearch.metadata;
